import React from "react";

import {
  Col,
  Collapse,
  Divider,
  Input,
  Row,
  Result,
  Spin,
  Card,
  Space,
  Typography,
  Empty,
} from "antd";
import { SearchOutlined, UserOutlined, HistoryOutlined, TrophyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { Customer } from "../../types/model";
import { useAntdTheme, useNotification } from "../../shared/hooks";

import { BasicInfo } from "./BasicInfo";
import { OrderHistory } from "./OrderHistory";
import { PointHistoryTable } from "./PointHistory";
import { AdjustCustomerPointsForm } from "./AdjustCustomerPoints/Form";

const { Panel } = Collapse;
const { Search } = Input;
const { Title, Text } = Typography;

type MemberManagementProp = {
  restaurantId: string;
};

export const MemberManagement = (props: MemberManagementProp) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notifyError } = useNotification();
  const [customer, setCustomer] = React.useState<Customer | null>(null);
  const [pointsVersion, setPointsVersion] = React.useState<number>(1);
  const [loading, setLoading] = React.useState<boolean>(false);

  const getCustomer = (emailOrPhone: string) => {
    setLoading(true);
    setCustomer(null);

    const url =
      `${API_URL}/v1/r/${props.restaurantId}/customers?eop=` + encodeURIComponent(emailOrPhone);
    getFetchT<Customer[]>(
      url,
      (customers: Customer[]) => {
        setLoading(false);

        if (customers.length === 0) {
          notifyError(t("member_not_found"), `{'message': '${t("member_not_found")}'}`);
        } else {
          // Special logic to hide backend bug that treat 163.com emails as "+1163266"
          // We can't remove the record from the db yet in case we need to rollback
          // backend.
          // TODO(kku): remove this hack once we know we won't rollback backend beyond
          // ea6ccde5b586303d860e44580a7007a52c419606
          if (customers[0].phone === "+1163266" || customers[0].phone === "+1126266") {
            customers[0].phone = "";
          }

          // TODO(kku): Support selecting from customer list.
          setCustomer(customers[0]);
        }
      },
      (err: any) => {
        setLoading(false);
        notifyError(t("unable_to_search_member"), err);
      }
    );
  };

  const onPointsUpdate = () => {
    setPointsVersion((prev) => prev + 1);
  };

  return (
    <div>
      {/* Search Section */}
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          background: token.colorBgContainer,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Space direction="vertical" style={{ width: "100%", gap: token.marginMD }}>
          <Space>
            <SearchOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Title level={4} style={{ margin: 0 }}>
              {t("search_member")}
            </Title>
          </Space>
          <Search
            placeholder={t("email_or_phone")}
            onSearch={getCustomer}
            enterButton={
              <Space>
                <SearchOutlined />
                {t("search")}
              </Space>
            }
            size="large"
            loading={loading}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        </Space>
      </Card>

      {/* Results Section */}
      {customer === null ? (
        loading ? (
          <div style={{ textAlign: "center", padding: token.paddingXL }}>
            <Spin size="large" />
          </div>
        ) : (
          <Card
            style={{
              borderRadius: token.borderRadiusLG,
              textAlign: "center",
              border: `1px solid ${token.colorBorder}`,
              marginBottom: token.marginXL,
            }}
            styles={{ body: { padding: token.paddingXL } }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text type="secondary" style={{ fontSize: token.fontSizeLG }}>
                    {t("no_result")}
                  </Text>
                  <Text type="secondary">{t("try_searching_again")}</Text>
                </Space>
              }
            />
          </Card>
        )
      ) : (
        <div>
          {/* Customer Basic Info */}
          <Card
            style={{
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorder}`,
              marginBottom: token.marginXL,
            }}
            styles={{ body: { padding: token.paddingLG } }}
          >
            <Space style={{ marginBottom: token.marginMD }}>
              <UserOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
              <Title level={4} style={{ margin: 0 }}>
                {t("member_information")}
              </Title>
            </Space>
            <BasicInfo customer={customer} />
          </Card>

          {/* History and Adjustments */}
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card
                style={{
                  borderRadius: token.borderRadiusLG,
                  boxShadow: token.boxShadowSecondary,
                  border: `1px solid ${token.colorBorder}`,
                  height: "100%",
                }}
                styles={{ body: { padding: token.paddingLG } }}
              >
                <Collapse
                  accordion
                  defaultActiveKey="point_history"
                  style={{ border: "none", background: "transparent" }}
                >
                  <Panel
                    header={
                      <Space>
                        <HistoryOutlined style={{ color: token.colorInfo }} />
                        <Text strong>{t("order_history")}</Text>
                      </Space>
                    }
                    key="order_history"
                    style={{
                      marginBottom: token.marginSM,
                      borderRadius: token.borderRadiusLG,
                      border: `1px solid ${token.colorBorder}`,
                    }}
                  >
                    <OrderHistory customer={customer} restaurantId={props.restaurantId} />
                  </Panel>
                  <Panel
                    header={
                      <Space>
                        <TrophyOutlined style={{ color: token.colorWarning }} />
                        <Text strong>{t("points_history")}</Text>
                      </Space>
                    }
                    key="point_history"
                    style={{
                      borderRadius: token.borderRadiusLG,
                      border: `1px solid ${token.colorBorder}`,
                    }}
                  >
                    <PointHistoryTable
                      customer={customer}
                      restaurantId={props.restaurantId}
                      version={pointsVersion}
                    />
                  </Panel>
                </Collapse>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <TrophyOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                    <Text strong style={{ fontSize: token.fontSizeLG }}>
                      {t("adjust_points")}
                    </Text>
                  </Space>
                }
                style={{
                  borderRadius: token.borderRadiusLG,
                  boxShadow: token.boxShadowSecondary,
                  border: `1px solid ${token.colorBorder}`,
                  height: "100%",
                }}
                styles={{ body: { padding: token.paddingLG } }}
              >
                <AdjustCustomerPointsForm
                  restaurantId={props.restaurantId}
                  email_or_phone={customer.email ? customer.email : customer.phone}
                  onSuccess={onPointsUpdate}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};
